/**
 * Job Form Component
 * Tabbed modal form for adding/editing jobs
 */

import React, { useState, useEffect } from 'react';
import { PRIORITY_OPTIONS } from '../../shared/constants/atsConstants';
import { crmDataService } from '../../../HR/CRM/utils/crmDataService';
import './JobForm.css';

const FORM_TABS = [
  { id: 'job-info', label: 'Job Information', icon: '💼' },
  { id: 'compensation', label: 'Compensation & Benefits', icon: '💰' }
];

// Reduced department list per user request
const JOB_DEPARTMENT_OPTIONS = [
  'CML',
  'Operations',
  'Support Center'
];

function JobForm({ job, onSave, onClose }) {
  const [activeTab, setActiveTab] = useState('job-info');
  const [formData, setFormData] = useState({
    job_title: '',
    location: '',
    department: '',
    sc_department: '',
    priority: '',
    hiring_manager: '',
    compensation_range_from: '',
    compensation_range_to: '',
    allowances: ['N/A'],
    job_notes: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);

  // Check if compensation range is invalid
  const isCompensationInvalid = () => {
    if (formData.compensation_range_from && formData.compensation_range_to) {
      const rangeFrom = parseFloat(formData.compensation_range_from);
      const rangeTo = parseFloat(formData.compensation_range_to);
      return rangeFrom >= rangeTo;
    }
    return false;
  };

  // Load stores on component mount
  useEffect(() => {
    const loadStores = async () => {
      try {
        setLoadingStores(true);
        const storesData = await crmDataService.getStores();
        const sortedStores = storesData.sort((a, b) =>
          (a.store_name || '').localeCompare(b.store_name || '')
        );
        setStores(sortedStores);
      } catch (err) {
        console.error('Failed to load stores:', err);
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    loadStores();
  }, []);

  useEffect(() => {
    if (job) {
      let allowancesArray = ['N/A'];
      if (job.allowances) {
        if (Array.isArray(job.allowances)) {
          allowancesArray = job.allowances;
        } else if (typeof job.allowances === 'string') {
          allowancesArray = job.allowances.split(',').map(a => a.trim()).filter(a => a);
          if (allowancesArray.length === 0) allowancesArray = ['N/A'];
        }
      }

      setFormData({
        job_title: job.job_title || '',
        location: job.location || '',
        department: job.department || '',
        sc_department: job.sc_department || '',
        priority: job.priority || '',
        hiring_manager: job.hiring_manager || '',
        compensation_range_from: job.compensation_range_from || '',
        compensation_range_to: job.compensation_range_to || '',
        allowances: allowancesArray,
        job_notes: job.job_notes || ''
      });
    }
  }, [job]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Clear SC Department if department is not Support Center
    if (field === 'department' && value !== 'Support Center') {
      setFormData(prev => ({ ...prev, [field]: value, sc_department: '' }));
    }
  };

  const handleAllowancesChange = (e) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);

    let finalSelection = options;
    if (options.includes('N/A') && options.length > 1) {
      finalSelection = ['N/A'];
    } else if (options.length === 0) {
      finalSelection = ['N/A'];
    }

    setFormData(prev => ({ ...prev, allowances: finalSelection }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.job_title?.trim()) {
      newErrors.job_title = 'Job title is required';
    }

    if (!formData.location?.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.department?.trim()) {
      newErrors.department = 'Department is required';
    }

    if (!formData.priority?.trim()) {
      newErrors.priority = 'Priority is required';
    }

    if (!formData.hiring_manager?.trim()) {
      newErrors.hiring_manager = 'Hiring manager is required';
    }

    if (!formData.compensation_range_from || formData.compensation_range_from === '') {
      newErrors.compensation_range_from = 'Minimum compensation is required';
    }

    if (!formData.compensation_range_to || formData.compensation_range_to === '') {
      newErrors.compensation_range_to = 'Maximum compensation is required';
    }

    if (formData.compensation_range_from && formData.compensation_range_to) {
      const rangeFrom = parseFloat(formData.compensation_range_from);
      const rangeTo = parseFloat(formData.compensation_range_to);

      if (rangeFrom >= rangeTo) {
        newErrors.compensation_range_to = 'Maximum compensation must be greater than minimum compensation';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      // Switch to tab with errors
      if (errors.job_title || errors.location || errors.department || errors.priority || errors.hiring_manager) {
        setActiveTab('job-info');
      } else if (errors.compensation_range_from || errors.compensation_range_to) {
        setActiveTab('compensation');
      }
      return;
    }

    try {
      setSaving(true);
      const dataToSave = {
        ...formData,
        allowances: formData.allowances.join(', ')
      };
      await onSave(dataToSave);
    } catch (err) {
      setErrors({ submit: 'Failed to save job. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'job-info':
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group">
                <label>
                  Job Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  className={errors.job_title ? 'error' : ''}
                />
                {errors.job_title && (
                  <span className="error-message">{errors.job_title}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  Location <span className="required">*</span>
                </label>
                <select
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  disabled={loadingStores}
                  className={errors.location ? 'error' : ''}
                >
                  <option value="">
                    {loadingStores ? '-- Loading Locations --' : '-- Select Location --'}
                  </option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>
                      {store.store_name}
                    </option>
                  ))}
                </select>
                {errors.location && (
                  <span className="error-message">{errors.location}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Department <span className="required">*</span>
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className={errors.department ? 'error' : ''}
                >
                  <option value="">-- Select Department --</option>
                  {JOB_DEPARTMENT_OPTIONS.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                {errors.department && (
                  <span className="error-message">{errors.department}</span>
                )}
              </div>

              {formData.department === 'Support Center' && (
                <div className="form-group">
                  <label>SC Department</label>
                  <input
                    type="text"
                    value={formData.sc_department}
                    onChange={(e) => handleChange('sc_department', e.target.value)}
                    placeholder="Enter SC Department"
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Priority <span className="required">*</span>
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className={errors.priority ? 'error' : ''}
                >
                  <option value="">-- Select Priority --</option>
                  {PRIORITY_OPTIONS.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
                {errors.priority && (
                  <span className="error-message">{errors.priority}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  Hiring Manager <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.hiring_manager}
                  onChange={(e) => handleChange('hiring_manager', e.target.value)}
                  className={errors.hiring_manager ? 'error' : ''}
                  placeholder="Enter hiring manager name"
                />
                {errors.hiring_manager && (
                  <span className="error-message">{errors.hiring_manager}</span>
                )}
              </div>
            </div>
          </div>
        );

      case 'compensation':
        return (
          <div className="tab-content">
            {isCompensationInvalid() && (
              <div className="alert alert-warning compensation-warning">
                <strong>Warning:</strong> Maximum compensation must be greater than minimum compensation.
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>
                  Compensation Range From <span className="required">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.compensation_range_from}
                  onChange={(e) => handleChange('compensation_range_from', e.target.value)}
                  placeholder="50000"
                  className={errors.compensation_range_from || isCompensationInvalid() ? 'error' : ''}
                />
                {errors.compensation_range_from && (
                  <span className="error-message">{errors.compensation_range_from}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  Compensation Range To <span className="required">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.compensation_range_to}
                  onChange={(e) => handleChange('compensation_range_to', e.target.value)}
                  placeholder="75000"
                  className={errors.compensation_range_to || isCompensationInvalid() ? 'error' : ''}
                />
                {errors.compensation_range_to && (
                  <span className="error-message">{errors.compensation_range_to}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>
                  Allowances
                  <span className="help-text"> (Hold Ctrl/Cmd to select multiple)</span>
                </label>
                <select
                  multiple
                  value={formData.allowances}
                  onChange={handleAllowancesChange}
                  className="multi-select-allowances"
                  size="3"
                >
                  <option value="N/A">N/A</option>
                  <option value="Car">Car</option>
                  <option value="Phone">Phone</option>
                </select>
                <span className="field-hint">
                  Selected: {formData.allowances.join(', ')}
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Job Notes</label>
                <textarea
                  value={formData.job_notes}
                  onChange={(e) => handleChange('job_notes', e.target.value)}
                  rows={4}
                  placeholder="Any additional notes about this job..."
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content job-form-modal">
        <div className="modal-header">
          <h2>{job ? 'Edit Job' : 'Add New Job'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="form-tabs">
          {FORM_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`form-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="job-form">
          {errors.submit && (
            <div className="alert alert-error">{errors.submit}</div>
          )}

          {/* Tab Content */}
          <div className="form-tab-content">
            {renderTabContent()}
          </div>

          {/* Fixed Actions at Bottom */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || isCompensationInvalid()}
            >
              {saving ? 'Saving...' : 'Save Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default JobForm;
